import { ReactNode } from 'react';
import { Theme } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import {
  getInspectionIsOutdated,
  getInspectionHasOutdatedGraphicZones,
  hasLegacyExcludedDifferences,
  getInspectionId,
  getBarcodeIsOutdatedByGradeImpact,
} from 'store';
import { useQuery } from '@redux-requests/react';
import { fetchInspection } from 'store/request/inspections/actions';
import { makeStyles } from 'tss-react/mui';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import type { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { CTFModuleTypes, InspectionStatuses } from 'types';
import { useFlags } from 'launchdarkly-react-client-sdk';

const useStyles = makeStyles()((theme: Theme) => {
  return {
    // MuiAlert is not yet part of the DeprecatedThemeOptions.Override type so we can't add it in the customizedTheme
    alert: {
      borderRadius: 0,
    },
    prepAlert: {
      fontWeight: 700,
    },
    alertList: {
      paddingLeft: theme.spacing(1.5),
      listStylePosition: 'outside',
    },
    alertMessage: {
      padding: 0,
    },
    warningIcon: {
      color: '#FF8C00',
      alignItems: 'center',
    },
    statusAlertMessage: {
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#663D00',
    },
    alertListItem: {
      padding: theme.spacing(0.5, 0),
    },
  };
});

// Determines whether an inspection uses any statuses that are no longer current
const inspectionUsesOutdatedStatuses = (statusData?: InspectionCustomStatuses | null): boolean => {
  if (!statusData) {
    return false;
  }
  const currentStatuses = statusData.currentStatuses || {};
  const allStatuses = statusData.allStatuses || {};
  const currentIds = new Set(Object.keys(currentStatuses));
  return Object.keys(allStatuses).some((id) => !currentIds.has(id));
};

const ResultsTabAlerts = () => {
  const { classes } = useStyles();
  const inspectionIsOutdated = useSelector(getInspectionIsOutdated);
  const hasExcludedDiffs = useSelector(hasLegacyExcludedDifferences);
  const outdatedGraphicsTool = useSelector(getInspectionHasOutdatedGraphicZones);
  const inspectionId = useSelector(getInspectionId);
  const isBarcodeOutdatedByGradeImpact = useSelector(getBarcodeIsOutdatedByGradeImpact);

  const {
    customStatuses: inspectionCustomStatuses,
    isLoading: customStatusesLoading,
    isError: customStatusesError,
  } = useInspectionCustomStatuses(inspectionId);

  const flags = useFlags();
  const inspectionQuery = useQuery({ type: fetchInspection });
  const inspectionErrors = inspectionQuery.data?.lastJob?.metadata?.errors ?? {};

  // remove duplicates when no live text, spelling and text inspections will have the same error
  // remove empty errors as they aren't errors
  const inspectionErrorsArray: (string | ReactNode)[] = Object.values(inspectionErrors);
  const filteredArray = inspectionErrorsArray.filter((error, index) => {
    return inspectionErrorsArray.indexOf(error) === index;
  });

  const outdatedGraphicsToolText = (
    <>
      This inspection was created using an outdated zone graphics tool. Please perform a new inspection to see the most
      accurate changes.
    </>
  );

  const outdatedExclusionsText = (
    <>This inspection has previously discarded items in the results. Please re-run a new inspection.</>
  );

  const outdatedStatusesText = (
    <span className={classes.statusAlertMessage}>
      This inspection was created using an outdated set of review statuses. Please perform a new inspection to leverage
      the latest (you will lose all marked reviews).
    </span>
  );

  const outdatedText = (
    <>
      Please re-run a new inspection to see the latest changes made in
      <span className={classes.prepAlert}> PREP </span>
      stage.
    </>
  );

  const outdatedBarcodeInspectionText = (
    <>
      This barcode inspection was created with an outdated version of Verify Compare. Please re-run a new inspection to
      see the latest changes.
    </>
  );

  // Embedded images graphics job failed but text succeeded
  const lastJob = inspectionQuery.data?.lastJob;
  const embeddedImagesGraphicsJobFailed =
    Boolean(lastJob?.metadata?.isEmbeddedImages) &&
    lastJob?.ctfJobs?.some((job) => job.type === CTFModuleTypes.graphics && job.status === InspectionStatuses.error) &&
    lastJob?.ctfJobs?.some((job) => job.type === CTFModuleTypes.text && job.status === InspectionStatuses.completed);

  const embeddedImagesFailureText = (
    <>
      <strong>Text results are ready.</strong> The embedded images unfortunately couldn't be processed due to complexity
      or volume, but you can still add them with the <em>Markup</em> tool.
    </>
  );

  // General case: some jobs failed but others succeeded
  // This should show independently of embedded images failure, so both warnings can appear together
  const failedJobs = lastJob?.ctfJobs?.filter((job) => job.status === InspectionStatuses.error) || [];
  const successfulJobs = lastJob?.ctfJobs?.filter((job) => job.status === InspectionStatuses.completed) || [];
  const hasPartialJobFailure =
    lastJob?.ctfJobs && lastJob.ctfJobs.length > 1 && failedJobs.length > 0 && successfulJobs.length > 0;

  // Format job type names for display
  const formatJobTypeName = (jobType: CTFModuleTypes): string => {
    const nameMap: Record<CTFModuleTypes, string> = {
      [CTFModuleTypes.text]: 'Text',
      [CTFModuleTypes.textMatch]: 'Text Match',
      [CTFModuleTypes.spelling]: 'Spelling',
      [CTFModuleTypes.graphics]: 'Graphics',
      [CTFModuleTypes.barcode]: 'Barcode',
      [CTFModuleTypes.braille]: 'Braille',
      [CTFModuleTypes.fontMetrics]: 'Font Size Check',
      [CTFModuleTypes.color]: 'Color',
      [CTFModuleTypes.markup]: 'Markups',
    };
    return nameMap[jobType] || String(jobType);
  };

  const failedJobNames = failedJobs.map((job) => formatJobTypeName(job.type));
  const partialJobFailureText = (
    <>
      The following inspection {failedJobNames.length === 1 ? 'job' : 'jobs'} failed:{' '}
      <strong>{failedJobNames.join(', ')}</strong>. Results from successful jobs are available below.
    </>
  );

  // Check if barcode inspection is outdated: either it predates the gradeImpact cutoff date
  // (feature-flag gated) or the differences carry no ISO/NonISO classification at all (legacy data).
  const barcodeCutoffDateFlag = flags.outdatedBarcodeCutoffDate;
  const barcodeJob = lastJob?.ctfJobs?.find((job) => job.type === CTFModuleTypes.barcode);
  const isBarcodeOutdatedByDate = (() => {
    if (!barcodeJob || !barcodeCutoffDateFlag) return false;
    const cutoffDate = new Date(barcodeCutoffDateFlag);
    const lastJobCreatedAt = lastJob?.createdAt ? new Date(lastJob.createdAt) : null;
    return Boolean(lastJobCreatedAt && lastJobCreatedAt < cutoffDate);
  })();
  if (isBarcodeOutdatedByDate || isBarcodeOutdatedByGradeImpact) {
    filteredArray.unshift(outdatedBarcodeInspectionText);
  }

  if (hasExcludedDiffs) {
    filteredArray.unshift(outdatedExclusionsText);
  }

  if (outdatedGraphicsTool) {
    filteredArray.unshift(outdatedGraphicsToolText);
  }

  if (inspectionIsOutdated) {
    filteredArray.unshift(outdatedText);
  }

  if (embeddedImagesGraphicsJobFailed) {
    filteredArray.unshift(embeddedImagesFailureText);
  }

  if (hasPartialJobFailure) {
    filteredArray.unshift(partialJobFailureText);
  }

  // Display banner when inspection uses statuses that are outdated
  const outdatedReviewStatuses =
    !customStatusesLoading && !customStatusesError ? inspectionUsesOutdatedStatuses(inspectionCustomStatuses) : false;

  if (outdatedReviewStatuses) {
    filteredArray.unshift(outdatedStatusesText);
  }

  if (filteredArray.length === 0) {
    return null;
  }

  let alertContents = <></>;
  if (filteredArray.length === 1) {
    alertContents = <p>{filteredArray[0]}</p>;
  } else if (filteredArray.length > 1) {
    // Update this list to MUI components when version 5+
    // Listitem does not have bullet point option sx={{display: 'list-item}} in MUI 4
    alertContents = (
      <ul className={classes.alertList}>
        {filteredArray.map((error, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`result-error-${index}`} className={classes.alertListItem}>
              {error}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Alert
      className={classes.alert}
      severity="warning"
      classes={{ message: classes.alertMessage, icon: classes.warningIcon }}
    >
      {alertContents}
    </Alert>
  );
};

export default ResultsTabAlerts;
